import { useEffect, useMemo, useState, type FC } from 'react';
import semver from 'semver';
import { ButtonField, Smokebreak } from '@wk/components-react16';
import { useQuery } from '@tanstack/react-query';
import { AgGridReact, type CustomCellRendererProps } from 'ag-grid-react';
import { type ColDef } from 'ag-grid-community';
import { flatten } from 'lodash';

import type { Evaluation, EvaluationCheckpoint } from '@/types';
import { fetchCheckpointAssessment } from '@/domains/evaluation/evaluationApi';
import { IconLoader2 } from '@tabler/icons-react';
import { exportToCsv, isJSON, isUrl } from '@/lib/utils';
import useToast from '@/shared/stores/toastStore';
import { getCheckpointVersion } from '@/lib/checkpointKeys';

import './AssessmentDataDrawer.css';

interface AssessmentDataProps {
  checkpoint: EvaluationCheckpoint;
  isOpen: boolean;
  evaluation: Evaluation | null;
  onClose: () => void;
  title?: string;
  localData?: Record<string, unknown>[];
  fetchRemote?: boolean;
  errorTitle?: string;
}

interface AssessmentDataState {
  isDownloadingCsv: boolean;
}

const convertBooleanValues = (value: unknown): unknown => {
  if (typeof value === 'boolean') {
    return value.toString();
  }

  if (Array.isArray(value)) {
    return value.map((item) => convertBooleanValues(item));
  }

  if (value && typeof value === 'object') {
    return Object.entries(value as Record<string, unknown>).reduce<Record<string, unknown>>(
      (acc, [key, val]) => {
        acc[key] = convertBooleanValues(val);
        return acc;
      },
      {},
    );
  }

  return value;
};

const AssessmentData: FC<AssessmentDataProps> = ({
  checkpoint,
  isOpen,
  evaluation,
  onClose,
  title = 'Checkpoint Assessment Data',
  localData,
  fetchRemote = true,
  errorTitle = 'Error fetching assessment data',
}) => {
  const { showToast } = useToast();
  const checkpointVersion = getCheckpointVersion(checkpoint.metadata);

  const [state, setState] = useState<AssessmentDataState>({
    isDownloadingCsv: false,
  });

  const handleStateUpdate = (newState: Partial<AssessmentDataState>) => {
    setState((prevState) => ({ ...prevState, ...newState }));
  };

  const shouldFetchAssessment = useMemo(() => {
    return Boolean(
      fetchRemote &&
      evaluation &&
      evaluation.schemaVersion &&
      semver.gte(evaluation.schemaVersion, '1.0.0'),
    );
  }, [evaluation, fetchRemote]);

  const {
    data: assessmentData,
    isLoading: isLoadingAssessment,
    error: assessmentError,
  } = useQuery({
    queryKey: [
      'assessmentData',
      checkpoint.metadata.id,
      checkpointVersion,
      evaluation?.id,
    ],
    queryFn: () =>
      fetchCheckpointAssessment(
        evaluation?.id || '',
        checkpoint.metadata.id,
        checkpointVersion,
      ),
    enabled: shouldFetchAssessment && Boolean(evaluation?.id) && isOpen,
    retry: false,
  });

  const fallbackRows = useMemo(() => {
    return localData ?? (checkpoint.assessmentData as Record<string, unknown>[] | undefined);
  }, [checkpoint.assessmentData, localData]);

  const onExportClick = async () => {
    handleStateUpdate({ isDownloadingCsv: true });
    await exportToCsv({
      options: {},
      rows: assessment || [],
      fileName: `${checkpoint?.metadata.displayName || checkpoint?.metadata.id
        }-${evaluation?.metadata.name}-${evaluation?.generatedAt}`,
    });
    handleStateUpdate({ isDownloadingCsv: false });
  };

  const assessment = useMemo(() => {
    const results = shouldFetchAssessment
      ? ((assessmentData?.data.data as Record<string, unknown>[] | undefined) ??
        fallbackRows)
      : fallbackRows;

    if (!results || results.length === 0) {
      return undefined;
    }

    return results.map((item) => {
      return convertBooleanValues(item) as Record<string, unknown>;
    });
  }, [assessmentData, fallbackRows, shouldFetchAssessment]);

  const assessmentSchema: ColDef[] = useMemo(() => {
    if (!assessment) {
      return [];
    }

    const orderedKeys = assessment.reduce<string[]>((acc, row) => {
      Object.keys(row).forEach((key) => {
        if (!acc.includes(key)) {
          acc.push(key);
        }
      });

      return acc;
    }, []);

    return flatten(
      orderedKeys.map((key) => {
        const jsonSubKeys = assessment.reduce<string[]>((acc, row) => {
          const value = row[key];

          if (!isJSON(value as string)) {
            return acc;
          }

          Object.keys(JSON.parse(value as string)).forEach((subKey) => {
            if (!acc.includes(subKey)) {
              acc.push(subKey);
            }
          });

          return acc;
        }, []);

        if (jsonSubKeys.length > 0) {
          return jsonSubKeys.map((subKey) => {
            return {
              headerName: `${key}.${subKey}`,
              field: `${key}.${subKey}`,
            };
          });
        }

        return {
          headerName: key,
          field: key,
          wrapText: true,
          cellRenderer: (params: CustomCellRendererProps) => {
            const value = params.value;
            if (typeof value === 'object' || Array.isArray(value)) {
              return (
                <>
                  <span className="py-2" id={`key-${params.node.id}-${key}`}>
                    {JSON.stringify(value, null, 2)}
                  </span>
                </>
              );
            }
            if (isUrl(value)) {
              return (
                <a href={value} className="text-blue-500" target="_blank">
                  {value}
                </a>
              );
            }
            return <span>{value}</span>;
          },
        };
      }),
    );
  }, [assessment]);

  useEffect(() => {
    if (assessmentError && (!fallbackRows || fallbackRows.length === 0)) {
      showToast('error', {
        title: errorTitle,
      });
    }
  }, [assessmentError, errorTitle, fallbackRows, showToast]);

  return (
    <div className="assessmentData">
      <Smokebreak isOpen={isOpen} position="right" onUserRequest={onClose}>
        <h2 slot="smokebreakHeader" className="p-3 font-semibold text-lg">
          {title}
        </h2>
        <div slot="smokebreakBody" className="flex flex-col w-full h-full">
          {isLoadingAssessment ? (
            <div className="flex-1 min-h-0 flex items-center justify-center">
              <IconLoader2 className="animate-spin" size={32} />
            </div>
          ) : (
            <div className="flex-1 min-h-0 p-3 w-full flex flex-col gap-3">
              {assessment && (
                <div className="flex justify-end">
                  <ButtonField>
                    <button onClick={onExportClick}>
                      Export to CSV
                      {state.isDownloadingCsv && (
                        <IconLoader2 className="animate-spin" size={16} />
                      )}
                    </button>
                  </ButtonField>
                </div>
              )}
              <div className="w-full flex-1 min-h-0">
                <div className="w-full h-full cg-ag-grid-field ag-theme-balham">
                  <AgGridReact
                    defaultColDef={{
                      wrapText: true,
                      autoHeight: true,
                    }}
                    autoSizeStrategy={{
                      type: 'fitGridWidth',
                      defaultMinWidth: 400,
                    }}
                    rowData={assessment || []}
                    columnDefs={assessmentSchema}
                    enableCellTextSelection={true}
                  />
                </div>
              </div>
            </div>
          )}
        </div>
      </Smokebreak>
    </div>
  );
};

export default AssessmentData;
